import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { keyBy } from 'lodash';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import defaultFilters, { FilterType } from './FilterDictionary';
import { deepMerge } from '@/_utils/utils';
import FiltersBar from './FiltersBar';
import baseDataConfig from '@/redux/slices/baseDataConfig';
import { getBillableCustomers, getNonBillableCustomers } from '@/redux/slices/constantsSlice';
import { selectBillableCustomers, selectNonBillableCustomers } from '@/redux/selectors/dataTableFiltersSelectors';
import { selectAllProfiles } from '@/redux/selectors/profileSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectHasOrderCategories } from '@/redux/selectors/constantsSelectors';
import FilterLayoutsModal from '@/components/_common/Filters/FilterLayoutsModal/FilterLayoutsModal';
import { useSavedFilterSettings } from '@/components/_common/Filters/hooks/useSavedFilterSettings';
import { FilterLayoutsFilter } from '@/redux/slices/slice_types';
import { mapFiltersById } from '@/components/_common/Filters/Filters.utils';

interface DataTableFiltersProps {
  disableExportReport?: boolean;
}

interface BuiltFilters {
  filters: FilterType[];
  defaultFilters: FilterType[];
  quickFilters?: FilterType[];
  defaultQuickFilters?: FilterType[];
  anyAllFilters?: FilterType[];
}

const getFilterId = (filter: FilterType) => {
  return filter.id ? filter.id : filter.label.toLowerCase().replace(/ /g, '_');
};

/**
 * This function filters out null filters and ensure all filters have IDs.
 */
const getValidFiltersWithIds = (filters: FilterType[], currentTableKey: string | null): FilterType[] => {
  return filters
    .filter((filter, index) => {
      if (!filter) {
        console.warn(`Filter in ${currentTableKey} at index ${index} is not in available filters: `, filters);
        return false;
      }
      return true;
    })
    .map((filter) => ({
      ...filter,
      id: getFilterId(filter)
    }));
};

/**
 * Pick filters from the array by a set of IDs.
 */
const pickQuickFilters = (quickFilterIds: Set<string>, filters: FilterType[]) => {
  const filtersById = mapFiltersById(filters);

  return [...quickFilterIds].reduce<FilterType[]>((acc, id) => {
    if (id in filtersById) {
      acc.push(filtersById[id]);
    }
    return acc;
  }, []);
};

const buildFiltersBasedOnLayout = (savedFilterSettings: FilterLayoutsFilter[], filters: FilterType[]) => {
  const filtersById = mapFiltersById(filters);
  const result: FilterType[] = [];

  savedFilterSettings.forEach((filter) => {
    // add filter to the result only if it exists in the client filters (in case the filters layout was saved when client filters had more items)
    if (filtersById[filter.id]) {
      result.push(filtersById[filter.id]);
    }
  });

  const addedFiltersById = mapFiltersById(result);

  // Add to the `result` filters that exist in `filters` array, but don't exist in the `layout`.
  // Those are the filters that were added after the layout was saved.
  filters.forEach((filter) => {
    if (!addedFiltersById[filter.id]) {
      result.push(filter);
    }
  });

  return result;
};

/**
 * The visibility of filters is determined based on the `is_hidden` property, and in the case with quick filter based one
 * `is_quick` property.
 */
const getVisibleFilters = (filters: FilterType[], savedFilterSettings?: FilterLayoutsFilter[], checkQuick = false) => {
  if (!savedFilterSettings) return filters;

  const result: FilterType[] = [];

  const savedFiltersById = keyBy(savedFilterSettings, 'id');

  filters.forEach((filter) => {
    const savedFilter = savedFiltersById[filter.id];
    if (savedFilter) {
      if (!savedFilter.is_hidden && (!checkQuick || savedFilter.is_quick)) {
        result.push(filter);
      }
    } else {
      result.push(filter);
    }
  });

  return result;
};

const DataTableFilters = ({ disableExportReport = false }: DataTableFiltersProps) => {
  const dispatch = useAppDispatch();
  const currentTableKey = useAppSelector(selectCurrentTableKey);
  const hasCategories = useAppSelector(selectHasOrderCategories);
  const { dataConfig } = useContext(ConfigContext);
  const configToUse = deepMerge(baseDataConfig, dataConfig);
  const tableConfig = currentTableKey ? configToUse[currentTableKey] : {};

  const billableCustomers = useAppSelector(selectBillableCustomers);
  const nonBillableCustomers = useAppSelector(selectNonBillableCustomers);
  const profiles = useAppSelector(selectAllProfiles);

  const clientFilters: FilterType[] = tableConfig.filters;

  const savedFilterSettings = useSavedFilterSettings();

  const hasBillableClientFilter = useMemo(() => {
    return clientFilters.some((e) => e.id === defaultFilters.billableClient.id);
  }, [clientFilters, defaultFilters]);

  const hasNonBillableCustomerFilter = useMemo(() => {
    return clientFilters.some((e) => e.id === defaultFilters.nonBillableCustomer.id);
  }, [clientFilters, defaultFilters]);

  const [isFilterLayoutsModalOpen, setIsFilterLayoutsModalOpen] = useState(false);

  const openFilterLayoutsModal = useCallback(() => {
    setIsFilterLayoutsModalOpen(true);
  }, []);

  const closeFilterLayoutsModal = useCallback(() => {
    setIsFilterLayoutsModalOpen(false);
  }, []);

  useEffect(() => {
    if (hasBillableClientFilter) {
      dispatch(getBillableCustomers() as any);
    }
  }, [hasBillableClientFilter]);

  useEffect(() => {
    if (hasNonBillableCustomerFilter) {
      dispatch(getNonBillableCustomers() as any);
    }
  }, [hasNonBillableCustomerFilter]);

  const buildFilters = (filters: FilterType[], savedFilterSettings?: FilterLayoutsFilter[], quickFilterIds?: Set<string>): FilterType[] => {
    const validFilters = getValidFiltersWithIds(filters, currentTableKey);

    let result: FilterType[];
    if (!savedFilterSettings) {
      if (quickFilterIds) {
        result = pickQuickFilters(quickFilterIds, validFilters);
      } else {
        result = validFilters;
      }
    } else {
      result = buildFiltersBasedOnLayout(savedFilterSettings, validFilters);
    }

    return result;
  };

  const builtFilters: BuiltFilters = useMemo(
    function calculateFilters() {
      let validFilters = clientFilters.filter((filter) => filter !== undefined);

      if (['jobs', 'orders'].includes(currentTableKey as string) && !hasCategories) {
        validFilters = validFilters.filter((filter) => filter.id !== 'category_filter');
      }

      let quickFilters: FilterType[] | undefined;
      let defaultQuickFilters: FilterType[] | undefined;

      if (tableConfig?.quickFilters) {
        if (!isSubset(validFilters, tableConfig.quickFilters)) {
          console.error('`quickFilters` should be a subset of `filters`. Otherwise filters bar might not work correctly.');
        }

        const quickFilterIds = new Set<string>(tableConfig.quickFilters.map((f: FilterType) => f.id));
        quickFilters = buildFilters(validFilters, savedFilterSettings, quickFilterIds);
        defaultQuickFilters = buildFilters(validFilters, undefined, quickFilterIds);
      }
      const anyAllFilters = tableConfig?.anyAllFilters ? buildFilters(tableConfig.anyAllFilters) : undefined;

      return {
        filters: buildFilters(validFilters, savedFilterSettings),
        defaultFilters: buildFilters(validFilters),
        quickFilters,
        defaultQuickFilters,
        anyAllFilters
      };
    },
    [billableCustomers, nonBillableCustomers, profiles, savedFilterSettings]
  );

  const visibleQuickFilters = useMemo(
    () => (builtFilters.quickFilters ? getVisibleFilters(builtFilters.quickFilters, savedFilterSettings, true) : undefined),
    [builtFilters.quickFilters, savedFilterSettings]
  );

  const visibleFilters = useMemo(() => getVisibleFilters(builtFilters.filters, savedFilterSettings), [builtFilters.filters, savedFilterSettings]);

  return (
    <>
      {/* TODO: Fix types. <FiltersBar /> should expect FilterType[] instead of FilterProps[] */}
      <FiltersBar
        filters={builtFilters.filters}
        visibleFilters={visibleFilters}
        visibleQuickFilters={visibleQuickFilters}
        anyAllFilters={builtFilters.anyAllFilters}
        disableExportReport={disableExportReport}
        openFilterLayoutsModal={openFilterLayoutsModal}
      />
      {builtFilters.filters.length > 0 && (
        <FilterLayoutsModal
          filters={builtFilters.filters}
          defaultFilters={builtFilters.defaultFilters}
          quickFilters={builtFilters.quickFilters}
          defaultQuickFilters={builtFilters.defaultQuickFilters}
          isOpen={isFilterLayoutsModalOpen}
          closeModal={closeFilterLayoutsModal}
        />
      )}
    </>
  );
};

const isSubset = (set: { id: string }[], probableSubset: { id: string }[]) => {
  const setIds = new Set(set.map(({ id }) => id));

  return probableSubset.every((item) => setIds.has(item.id));
};

export default DataTableFilters;
